import type {
  CreateIssueType,
  CreatePriority,
  DeleteIssueType,
  IssueType,
  Priority,
  Reorder,
  UpdateIssueType,
  UpdatePriority,
} from '@servicetim/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { adminPath, useApiContext } from './client';

const issueTypesPath = (projectId: string) => adminPath(`/projects/${projectId}/issue-types`);
const prioritiesPath = (projectId: string) => adminPath(`/projects/${projectId}/priorities`);

/* ----------------------------- issue types ----------------------------- */

/** `GET /admin/v1/projects/{id}/issue-types` (array, ordered by position). */
export function useIssueTypes(projectId: string | undefined) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.project(projectId ?? '').issueTypes,
    queryFn: ({ signal }) =>
      request<IssueType[]>('GET', issueTypesPath(projectId ?? ''), { signal }),
    enabled: projectId !== undefined,
  });
}

export function useCreateIssueType(projectId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (input: CreateIssueType) =>
      request<IssueType>('POST', issueTypesPath(projectId), { body: input }),
    onSuccess: () =>
      invalidate(keys.project(projectId).issueTypes, keys.projects.detail(projectId)),
  });
}

export function useUpdateIssueType(projectId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateIssueType }) =>
      request<IssueType>('PATCH', `${issueTypesPath(projectId)}/${id}`, { body: patch }),
    onSuccess: () =>
      invalidate(keys.project(projectId).issueTypes, keys.projects.detail(projectId)),
  });
}

/** `DELETE .../issue-types/{id}` with optional `moveToIssueTypeId` once tickets exist. */
export function useDeleteIssueType(projectId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ id, ...body }: DeleteIssueType & { id: string }) =>
      request<unknown>('DELETE', `${issueTypesPath(projectId)}/${id}`, {
        query: { moveTo: body.moveToIssueTypeId },
      }),
    onSuccess: () =>
      invalidate(
        keys.project(projectId).issueTypes,
        keys.projects.detail(projectId),
        keys.jobs.all,
      ),
  });
}

/** `POST .../issue-types/order` with the complete id list. */
export function useReorderIssueTypes(projectId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (input: Reorder) =>
      request<IssueType[]>('POST', `${issueTypesPath(projectId)}/order`, { body: input }),
    onSuccess: () =>
      invalidate(keys.project(projectId).issueTypes, keys.projects.detail(projectId)),
  });
}

/* ----------------------------- priorities ----------------------------- */

/** `GET /admin/v1/projects/{id}/priorities` (array, ordered by rank). */
export function usePriorities(projectId: string | undefined) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.project(projectId ?? '').priorities,
    queryFn: ({ signal }) =>
      request<Priority[]>('GET', prioritiesPath(projectId ?? ''), { signal }),
    enabled: projectId !== undefined,
  });
}

export function useCreatePriority(projectId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (input: CreatePriority) =>
      request<Priority>('POST', prioritiesPath(projectId), { body: input }),
    onSuccess: () =>
      invalidate(keys.project(projectId).priorities, keys.projects.detail(projectId)),
  });
}

export function useUpdatePriority(projectId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdatePriority }) =>
      request<Priority>('PATCH', `${prioritiesPath(projectId)}/${id}`, { body: patch }),
    onSuccess: () =>
      invalidate(keys.project(projectId).priorities, keys.projects.detail(projectId)),
  });
}

export function useDeletePriority(projectId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (id: string) => request<unknown>('DELETE', `${prioritiesPath(projectId)}/${id}`),
    onSuccess: () =>
      invalidate(keys.project(projectId).priorities, keys.projects.detail(projectId)),
  });
}

/** `POST .../priorities/order` with the complete id list. */
export function useReorderPriorities(projectId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (input: Reorder) =>
      request<Priority[]>('POST', `${prioritiesPath(projectId)}/order`, { body: input }),
    onSuccess: () =>
      invalidate(keys.project(projectId).priorities, keys.projects.detail(projectId)),
  });
}
